import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { List, Icon } from '@pella/sash-framework';
import { useMediaQuery } from 'react-responsive';
import { CTA } from '../..';
import { useOktaAuth } from '../../../hooks/use-okta-auth';
import { useBranchContext } from '../../../context/BranchContext';
import { useBranchId } from '../../../hooks/use-branch-id';
import { useBranchData } from '../../../hooks/use-branch-data';
import { isValidArray } from '../../utilities/validations';

import BackButton from './back-button';
import LocationButton from '../../common/header/location-button';
import ProductMenu from './product-menu';
import PerformanceMenu from './performance-menu';
import ProMenu from './pro-menu';

const MobileMenu = ({ showMenu, data }) => {
	const isMobile = useMediaQuery({ query: '(max-width: 1279px)' });
	const { profile } = useOktaAuth();
	const [isPro, setIsPro] = useState(false);
	const [isClient, setIsClient] = useState(false);
	const { currentBranch } = useBranchContext();
	const branchId = useBranchId();
	const branchData = useBranchData();

	useEffect(() => setIsClient(true), []);

	useEffect(() => {
		if (profile.isPro) {
			setIsPro(true);
		} else {
			setIsPro(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [profile.isPro]);

	const onMenuItemClick = (e) => {
		const menu = document.getElementById('js-mobileMenu');
		const parent = e.currentTarget.parentNode.parentNode;
		const siblings = e.currentTarget.parentNode.childNodes;

		if (siblings && siblings.length > 1) {
			menu.classList.add('!overflow-hidden');
			parent.classList.add('!overflow-hidden');
			siblings[1].classList.add('visible', 'overflow-y-auto', 'overflow-x-hidden', 'right-0');
			siblings[1].classList.remove('invisible', '-right-80', 'overflow-hidden');
		}
	};

	return (
		<nav
			id='js-mobileMenu'
			className={`mobileMenu fixed top-[59px] z-20 h-[calc(100vh-59px)] w-full overflow-y-auto overflow-x-hidden bg-neutral-25 pb-7 motion-safe:transition-all motion-safe:duration-500 sm:top-[63px] sm:h-[calc(100vh-63px)] sm:max-w-xs xl:hidden ${showMenu ? 'mobileMenu-active visible right-0' : 'invisible -right-full sm:-right-80'}`}
			aria-label='Mobile Menu'
			aria-hidden={!showMenu}
		>
			{isClient && isMobile && <LocationButton />}

			<List
				bullets='none'
				className='mobileMenu-primary'
			>
				{isValidArray(data?.product_menu) &&
					data.product_menu.map((primary, index) => {
						const views = { product: ProductMenu, performance: PerformanceMenu, pro: ProMenu };
						const MenuView = views[primary.menu_type] ?? null;
						let directLink = primary.direct_link;

						if (currentBranch?.branch_url && primary.direct_link === '/where-to-buy/') {
							directLink = currentBranch?.branch_url + 'showrooms/';
						}

						return (
							<li
								key={index}
								className='bg-white'
							>
								{directLink && (
									<Link
										className='block w-full px-4 py-3 text-left text-lg font-semibold !leading-tight decoration-transparent'
										to={directLink}
									>
										{primary.primary_heading}
									</Link>
								)}

								{!directLink && (
									<button
										onClick={onMenuItemClick}
										className='relative block w-full px-4 py-3 text-left text-lg font-semibold !leading-tight text-black decoration-transparent'
										data-title={primary.primary_heading}
										aria-label='default'
									>
										{primary.primary_heading}
										<Icon
											name='keyboard_arrow_right'
											className='absolute right-4 top-1/2 -translate-y-1/2'
										/>
									</button>
								)}

								{primary.secondary_nav?.length > 1 && (
									<List
										bullets='none'
										className='mobileMenu-secondary invisible absolute -right-80 top-0 z-[11] h-[calc(100vh-59px)] w-full overflow-hidden bg-white shadow-xl motion-safe:transition-all motion-safe:duration-500 sm:h-[calc(100vh-63px)]'
										key={index}
									>
										<BackButton title={primary.primary_heading} />

										{primary.secondary_nav.map((secondary, index) => (
											<li key={index}>
												{secondary.direct_link && (
													<Link
														to={secondary.direct_link}
														className='flex items-center px-4 py-3 text-sm font-semibold no-underline'
													>
														<Icon
															name='open_in_new'
															className='mr-1'
															size={16}
														/>
														<span>{secondary.secondary_heading}</span>
													</Link>
												)}
												{!secondary.direct_link && (
													<button
														onClick={onMenuItemClick}
														className='relative w-full px-4 py-3 text-left text-sm font-semibold'
														data-title={primary.primary_heading}
														aria-label={secondary.secondary_heading}
													>
														{secondary.secondary_heading}
														<Icon
															name='keyboard_arrow_right'
															className='absolute right-4 top-1/2 -translate-y-1/2'
														/>
													</button>
												)}

												<MenuView
													data={secondary}
													onMenuItemClick={onMenuItemClick}
													parent={secondary.secondary_heading}
												>
													<BackButton
														title={secondary.secondary_heading}
														buttonText={primary.primary_heading}
													/>
												</MenuView>
											</li>
										))}
									</List>
								)}

								{primary.secondary_nav?.length === 1 && (
									<MenuView
										data={primary.secondary_nav[0]}
										onMenuItemClick={primary.menu_type === 'product' ? onMenuItemClick : null}
										parent={primary.menu_type === 'product' ? primary.primary_heading : null}
									>
										<BackButton title={primary.primary_heading} />
									</MenuView>
								)}
							</li>
						);
					})}

				{isValidArray(data?.global_nav?.cta) && (
					<li>
						<List
							bullets='none'
							className='divide-y-2 divide-neutral-50 px-4 py-2'
						>
							{data.global_nav.cta
								.filter((cta) => !cta.button_style)
								.map((link, index) => (
									<li key={index}>
										<CTA
											data={{ ...link, material_icon: 'open_in_new' }}
											className='inline-flex items-center py-2 text-sm no-underline [&>span]:mr-2 [&>span]:text-sm'
										/>
									</li>
								))}
						</List>

						{data.global_nav.cta
							.filter((cta) => cta.button_style)
							.map((cta, index) => {
								if (isPro && index === 0) return null;
								if (!isPro && index === 1) return null;

								if (currentBranch?.hide_rta && index === 0) return null;
								if (branchData?.find((data) => data.branch_retail?.includes(branchId))?.hide_rta && index === 0) return null;

								return (
									<div
										className='px-4'
										key={index}
									>
										<CTA
											data={cta}
											size='sm'
											wide
										/>
									</div>
								);
							})}
					</li>
				)}
			</List>
		</nav>
	);
};

MobileMenu.propTypes = {
	showMenu: PropTypes.bool,
	data: PropTypes.object,
};

export default MobileMenu;
